import { createSlice } from '@reduxjs/toolkit'
import camera_settings_schema from '~/schemas/camera_settings_schema'
import { IS_MOBILE } from '~/lib/constants'

const LAST_CAMERA_KEY = 'oylo:last_camera_device'

const lastCamera = () => {
	try {
		return localStorage.getItem(LAST_CAMERA_KEY)
	} catch {
		return null
	}
}

/** The device picked here last, which the next session's `camera` opens. */
export const rememberCamera = deviceId => {
	try {
		localStorage.setItem(LAST_CAMERA_KEY, deviceId)
	} catch {
		// storage unavailable
	}
}

const logger = console.scope('camera')

const initialState = {
	available: IS_MOBILE
		? [] // Mobile: no 'none' option, will auto-select first available camera
		: [
				{ deviceId: 'none', label: 'None' },
				{ deviceId: 'screen', label: 'Screen Capture' },
			],
	// This machine's camera: the device a `camera` source opens. Null until enumeration finds one.
	device: null,
	camera_settings: null,
	facingMode: null, // 'user' (front) or 'environment' (back)
	loading: false, // true while acquiring camera (including retries)
	zoomCapability: null, // { min, max, step } or null if unsupported
	zoom: 1, // current zoom level
	blocked: false, // true when camera permission denied (NotAllowedError)
}

const cameraSlice = createSlice({
	name: 'camera',
	initialState,
	reducers: {
		setAvailableCameras: (s, { payload }) => {
			s.available = payload
			// The kept device, else the one last used here, else the first.
			if (!payload.some(c => c.deviceId === s.device)) {
				const last = lastCamera()
				const next = payload.find(c => c.deviceId === last)?.deviceId ?? payload[0]?.deviceId ?? null
				if (next !== s.device) logger.info('camera device', `'${s.device}' > '${next}'`)
				s.device = next
			}
			if (!IS_MOBILE) {
				s.available.unshift({ deviceId: 'none', label: 'None' })
				s.available.push({ deviceId: 'video_file', label: 'Video File' })
				s.available.push({ deviceId: 'screen', label: 'Screen Capture' })
			}
		},
		setCameraDevice: (s, { payload }) => {
			s.device = payload
		},
		setCameraSettings: (s, { payload }) => {
			if (!payload) {
				s.camera_settings = null
				s.facingMode = null
				return
			}
			const { capabilities, settings } = payload
			// Store facingMode for front camera detection
			s.facingMode = settings.facingMode || null
			let found = false
			s.camera_settings = {}
			camera_settings_schema.forEach(({ name, label, type, step, kind, parent }) => {
				if (name in settings) {
					found = true
					const cap = capabilities[name]
					if (Array.isArray(cap)) {
						s.camera_settings[name] = {
							options: cap,
							value: 'continuous',
							initial: 'continuous',
						}
					} else {
						s.camera_settings[name] = {
							name,
							type,
							kind,
							label,
							minimum: capabilities[name].min,
							maximum: capabilities[name].max,
							step: step || capabilities[name].step,
							value: settings[name],
							initial: settings[name],
							parent,
						}
						if (parent) {
							s.camera_settings[name].disabled = settings[parent] !== 'manual'
						}
					}
				}
			})
			if (!found) {
				s.camera_settings = {}
			}
		},
		setCameraSetting: (s, { payload }) => {
			const [key, value] = payload
			if (key in s.camera_settings) {
				s.camera_settings[key].value = value
			}

			Object.values(s.camera_settings).forEach(a => {
				if (a.parent) {
					a.disabled = s.camera_settings[a.parent].value !== 'manual'
				}
			})
		},
		setCameraLoading: (s, { payload }) => {
			s.loading = payload
		},
		setZoomCapability: (s, { payload }) => {
			s.zoomCapability = payload
			// Reset zoom to 1 when capability changes (e.g., camera switch)
			s.zoom = 1
		},
		setZoom: (s, { payload }) => {
			s.zoom = payload
		},
		setCameraBlocked: (s, { payload }) => {
			s.blocked = payload
		},
	},
})

export const {
	setAvailableCameras,
	setCameraDevice,
	setCameraSettings,
	setCameraSetting,
	setCameraLoading,
	setZoomCapability,
	setZoom,
	setCameraBlocked,
} = cameraSlice.actions

// Thunks
export const applyCameraSetting = (name, value) => async (dispatch, getState) => {
	const { camera_settings } = getState().camera
	if (!camera_settings?.[name]) return

	const track = window.camera_stream?.getVideoTracks()[0]
	if (!track) return

	dispatch(setCameraSetting([name, value]))

	await track.applyConstraints({
		[name]: value,
	})
}

export default cameraSlice.reducer

if (import.meta.hot) {
	import.meta.hot.dispose(() => {
		if (window.camera_stream) {
			window.camera_stream.getTracks().forEach(t => t.stop())
			window.camera_stream = null
		}
	})
}
